"use client";

import React from "react";
import { useApp } from "../AppProvider";

export default function MediaBlock({
  image,
  position,
  caption_en,
  caption_ar,
}: {
  image: any;
  position: "full" | "center" | "left" | "right";
  caption_en?: string;
  caption_ar?: string;
}) {
  const { isRTL } = useApp();
  if (!image) return null;

  // Build the image URL — handle both populated objects and plain strings
  let imageUrl: string = typeof image === "string" ? image : (image.url ?? "");

  // Normalise Payload's internal API URL to the public /media path.
  // Handles both absolute URLs (http://localhost:3000/api/media/file/foo.png)
  // and relative ones (/api/media/file/foo.png).
  const apiSegment = "/api/media/file/";
  if (imageUrl.includes(apiSegment)) {
    const filename = imageUrl.split(apiSegment).pop() ?? "";
    imageUrl = `/media/${filename}`;
  }

  if (!imageUrl) return null;

  const caption = isRTL ? caption_ar || caption_en : caption_en;

  const containerStyle: React.CSSProperties = {
    marginBottom: "4rem",
    marginTop: "2rem",
    position: "relative",
    width: "100%",
  };

  const imageWrapperStyle: React.CSSProperties = {
    borderRadius: "1.5rem",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    transition: "transform 0.4s ease",
  };

  if (position === "center") {
    containerStyle.maxWidth = "800px";
    containerStyle.marginInline = "auto";
  } else if (position === "left" || position === "right") {
    const isActuallyLeft = isRTL ? position === "right" : position === "left";
    containerStyle.maxWidth = "450px";
    containerStyle.float = isActuallyLeft ? "left" : "right";
    containerStyle.marginBottom = "2rem";
    if (isActuallyLeft) {
      containerStyle.marginInlineEnd = "2.5rem";
    } else {
      containerStyle.marginInlineStart = "2.5rem";
    }
  }

  return (
    <figure style={containerStyle}>
      <div style={imageWrapperStyle} className="media-block-image-wrapper">
        {/* Plain <img> is used here intentionally — avoids next/image hostname
            configuration requirements for CMS-uploaded content-block images. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl}
          alt={image.alt || caption || "Qumrah Media"}
          loading="lazy"
          style={{ width: "100%", height: "auto", display: "block", objectFit: "cover" }}
        />
      </div>
      {caption && (
        <figcaption
          style={{
            marginTop: "1rem",
            fontSize: "0.9rem",
            color: "var(--text-secondary)",
            textAlign: position === "center" ? "center" : isRTL ? "right" : "left",
            fontStyle: "italic",
            opacity: 0.8,
          }}
        >
          {caption}
        </figcaption>
      )}
      {/* Clear floats */}
      <div style={{ clear: "both" }} />
    </figure>
  );
}
